<template>
  <div>
    <header
      class="flex flex-col md:flex-row md:items-center md:justify-between"
    >
      <h1 class="heading-1">{{ i18n(translations.wishListTitle) }}</h1>
      <div class="flex flex-col gap-6 md:flex-row md:space-x-12">
        <RevButton
          class="mt-24 md:mt-0"
          variant="secondary"
          @click="handleCsvAction"
        >
          {{ i18n(translations.buttonImportExport) }}
        </RevButton>
        <RevButton
          v-if="!displayNoOffer"
          class="md:mt-0"
          :to="{
            name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
          }"
          variant="primary"
        >
          {{ i18n(translations.buttonCreate) }}
        </RevButton>
      </div>
    </header>
    <Filters
      :loading-button="data.filtersLoadingButton"
      @filter="handleFilters"
    />

    <div
      v-if="!data.isLoading && countryCodeOptions.length"
      ref="scrollContainer"
      class="mb-32 mt-68 flex flex-col md:mb-32 md:mt-44 md:flex-row"
    >
      <p class="text-static-default-mid heading-2 mb-[1.6rem] md:mb-0">
        {{ i18n(translations.savedProductsCount, { total: pagination.count }) }}
      </p>
      <RevInputSelect
        id="marketCode"
        v-model="data.marketCode"
        class="w-[16rem] md:ml-auto"
        :label="i18n(translations.marketInputLabel)"
        name="marketCode"
        :options="countryCodeOptions"
        @update:model-value="() => handleMarketCodeChange()"
      />
    </div>

    <!--  Loading for "{number} Listings" -->
    <div v-if="data.isLoading" data-test="listing-skeleton">
      <div class="mb-32 mt-68 md:mb-32 md:mt-44">
        <RevSkeleton
          class="text-static-default-mid heading-2 mb-[1.6rem]"
          height="50px"
          shape="rectangle"
        >
          {{ i18n(translations.savedProductsCount, { total: 0 }) }}
        </RevSkeleton>
      </div>
    </div>

    <!--  Opportunities -->
    <ClientOnly>
      <aside
        v-if="
          isOpportunitiesEnabled &&
          !isLoadingOpportunities &&
          totalOpportunities > 0
        "
      >
        <RevButtonCard
          class="flex w-full items-center px-32 py-18 text-left"
          :class="$style.opportunitiesCard"
          :to="{
            name: ROUTE_NAMES.BUYBACK.OPPORTUNITIES,
          }"
        >
          <div class="grow">
            <RevTag
              :label="i18n(translations.opportunitiesTagNew)"
              variant="info"
            />
            <h2 class="heading-2">
              {{
                i18n(translations.opportunitiesTitle, {
                  total: totalOpportunities,
                })
              }}
            </h2>
            <p>
              {{ i18n(translations.opportunitiesDescription) }}
            </p>
          </div>
          <div>
            <IconChevronRight />
          </div>
        </RevButtonCard>
      </aside>
    </ClientOnly>

    <div class="mb-72 mt-68 md:mt-44">
      <div v-if="data.isLoading" data-test="listing-skeleton">
        <LoadingSkeleton class="mb-52 mt-32 md:mb-36" />
        <LoadingSkeleton class="mb-52 md:mb-36" />
      </div>

      <div v-if="displayNoOffer">
        <NoOffer v-if="isMobileScreen" />
        <RevContainer v-else>
          <NoOffer />
        </RevContainer>
      </div>

      <div v-if="displayQuotations">
        <OfferDetails
          v-for="quote in data.quotes"
          :key="`${quote.product.productId}-${quote.marketCode}`"
          class="mb-52 md:mb-36"
          :quote
          @update-timeout="openTimeoutToast"
        />
        <Pagination
          v-if="displayPagination"
          class="flex flex-1 lg:mt-0"
          :current-page="pagination?.currentPage"
          :page-count="pages"
          @click="handlePaginationUpdate"
        />
      </div>
    </div>
    <CsvModal
      :is-importing="data.isModalActionLoading"
      :name="MODAL_NAMES.REFURBISHER_WISHLIST_CSV"
      @submit-export="handleCsvExport"
      @submit-import="handleCsvImport"
    />
  </div>
</template>

<script setup lang="ts">
import {
  type Error,
  getApiErrorMessage,
  useAsyncData,
  useRuntimeConfig,
} from '#imports'
import { computed, reactive, useTemplateRef, watch } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import MODAL_NAMES from '@backmarket/nuxt-layer-buyback/utils/modals'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevButtonCard } from '@ds/components/ButtonCard'
import { RevContainer } from '@ds/components/Container'
import { RevInputSelect } from '@ds/components/InputSelect'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevTag } from '@ds/components/Tag'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import type { BuyBackQuote } from '~/api/quotes/types'
import { VALUE_FILTER_ALL } from '~/constants/buyback'
import { ROUTE_NAMES } from '~/constants/routes'
import { useFetchSourcingOpportunities } from '~/scopes/buyback/pages/opportunities/composables/useFetchSourcingOpportunities'
import NoOffer from '~/scopes/buyback/pages/wishlist/components/NoOffer/NoOffer.vue'

import { getCountryCodeFromLocale } from '../../utils/buyback.utils'

import type { Pagination, WishListData } from './WishList.types'
import translations from './Wishlist.translations'
import type {
  CsvExportFormValues,
  CsvImportFormValues,
} from './components/CsvModal/CsvModal.types'
import CsvModal from './components/CsvModal/CsvModal.vue'
import type { FilterEventPayload } from './components/Filters/Filters.types'
import Filters from './components/Filters/Filters.vue'
import LoadingSkeleton from './components/LoadingSkeleton/LoadingSkeleton.vue'
import OfferDetails from './components/OfferDetails/OfferDetails.vue'

const i18n = useI18n()
const logger = useLogger()
const breakpoint = useClientOnlyComputedBreakpoint()
const { openErrorToast, openSuccessToast, openNeutralToast } = useTheToast()
const $httpFetch = useHttpFetch()
const scrollContainer = useTemplateRef<HTMLElement>('scrollContainer')
const { FF_BO_SELLER_TRADE_IN_OPPORTUNITIES } = useRuntimeConfig().public

const isOpportunitiesEnabled = FF_BO_SELLER_TRADE_IN_OPPORTUNITIES === true

const { opportunities, status: opportunitiesStatus } =
  useFetchSourcingOpportunities()

const totalOpportunities = computed(() => opportunities.value?.totalCount ?? 0)

const isLoadingOpportunities = computed(
  () => opportunitiesStatus.value === 'pending',
)

const data = reactive<WishListData>({
  availableMarkets: [],
  quotes: [],
  marketCode: VALUE_FILTER_ALL,
  filters: {},
  filtersLoadingButton: '',
  isLoading: true,
  isModalActionLoading: false,
})

const pagination = reactive<Pagination>({
  pageSize: 10,
  currentPage: 1,
  count: 0,
  previous: null,
  next: null,
})

const { data: quotesResponse, error: quotesError } = useAsyncData(
  () => {
    return $httpFetch('/buyback-quotes/api/v1/quotes', {
      query: {
        pageNumber: pagination.currentPage,
        pageSize: pagination.pageSize,
      },
    })
  },
  { server: false },
)

const { data: marketsResponse, error: marketsError } = useAsyncData(
  () => {
    return $httpFetch('/bm/sourcing/merchant')
  },
  { server: false },
)

watch(quotesError, () => {
  if (!quotesError.value) return

  openErrorToast({
    content: getApiErrorMessage(quotesError.value as unknown as Error),
  })

  logger.error('[BUYBACK][API][QUOTES] Error getting quotes', {
    error: quotesError.value,
    owners: ['bot-squad-circularity-offer-front'],
  })

  data.isLoading = false
})

watch(marketsError, () => {
  if (!marketsError.value) return

  openErrorToast({
    content: getApiErrorMessage(marketsError.value as unknown as Error),
  })

  logger.error('[BUYBACK][API][QUOTES] Error getting markets', {
    error: marketsError.value,
    owners: ['bot-squad-circularity-offer-front'],
  })

  data.isLoading = false
})

watch(
  [quotesResponse, marketsResponse],
  () => {
    if (!quotesResponse.value || !marketsResponse.value) return

    pagination.count = quotesResponse.value.totalCount
    data.quotes = quotesResponse.value.quotes

    data.availableMarkets = marketsResponse.value.markets.map((locale) => {
      const value = getCountryCodeFromLocale(locale) as string

      return { label: value, value }
    })

    data.isLoading = false
  },
  { immediate: true, deep: true },
)

const isMobileScreen = computed(() => breakpoint.value < Breakpoint.MD)
const displayNoOffer = computed(() => !data.isLoading && isEmpty(data.quotes))
const displayQuotations = computed(
  () => !data.isLoading && !isEmpty(data.quotes),
)
const displayPagination = computed(() => pagination.count > pagination.pageSize)
const cleanedFiltersPayload = computed(() =>
  Object.entries(data.filters).reduce((acc, filters) => {
    const [key, value] = filters

    if (isEmpty(value) || value === VALUE_FILTER_ALL) {
      return { ...acc }
    }

    if (key === 'grades') {
      return { ...acc, grades: [value] }
    }

    // trim() input values to prevent backends errors
    return { ...acc, [key]: value.trim() }
  }, {}),
)
const countryCodeOptions = computed(() => [
  { label: i18n(translations.allMarkets), value: VALUE_FILTER_ALL },
  ...data.availableMarkets,
])
const pages = computed(() => Math.ceil(pagination.count / pagination.pageSize))

function handleCsvAction() {
  openModal(MODAL_NAMES.REFURBISHER_WISHLIST_CSV)
}

const payloadMarketCode = computed(() =>
  data.marketCode && data.marketCode !== VALUE_FILTER_ALL
    ? { marketCodes: data.marketCode }
    : {},
)

async function getQuotes({ page = 1, fullReload = true } = {}): Promise<
  BuyBackQuote[] | null
> {
  data.isLoading = fullReload

  try {
    const { totalCount, quotes } = await $httpFetch(
      '/buyback-quotes/api/v1/quotes',
      {
        query: {
          ...cleanedFiltersPayload.value,
          ...payloadMarketCode.value,
          pageNumber: page,
          pageSize: pagination.pageSize,
        },
      },
    )

    pagination.count = totalCount
    pagination.currentPage = page

    return quotes
  } catch (error) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error getting quotes', {
      error: error as HttpApiError,
      owners: ['bot-squad-circularity-offer-front'],
    })

    return null
  } finally {
    data.isLoading = false
  }
}

async function handleFilters(args: FilterEventPayload) {
  const { actionButton, filters } = args

  data.filtersLoadingButton = String(actionButton)
  data.filters = filters
  data.quotes = await getQuotes()
  data.filtersLoadingButton = ''
}

async function handlePaginationUpdate(page: { page: number }) {
  data.quotes = await getQuotes(page)
  scrollToTop()
}

async function handleMarketCodeChange() {
  data.quotes = await getQuotes()
  data.filtersLoadingButton = ''
}

async function handleCsvImport(values: CsvImportFormValues) {
  data.isModalActionLoading = true
  const { encoding, csvFile, delimiter } = values

  const body = new FormData()

  body.append('csvFile', csvFile[0])
  body.append('encoding', encoding)
  body.append('delimiter', delimiter)

  try {
    const response = await $httpFetch('/bm/catalog/buyback/listings/import', {
      method: 'POST',
      body,
    })

    openSuccessToast({
      title: i18n(translations.toastExportSuccessTitle),
      content: i18n(translations.toastExportSuccessMessage, {
        taskNumber: response.task,
      }),
    })
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error importing listings', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-offer-front'],
    })
  } finally {
    data.isModalActionLoading = false
    closeModal()
  }
}

async function handleCsvExport(values: CsvExportFormValues) {
  data.isModalActionLoading = true
  const { encoding } = values

  try {
    const response = await $httpFetch('/bm/catalog/buyback/listings/export', {
      method: 'POST',
      body: { encoding },
    })

    openSuccessToast({
      title: i18n(translations.toastExportSuccessTitle),
      content: i18n(translations.toastExportSuccessMessage, {
        taskNumber: response.task,
      }),
    })
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error exporting listings', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-offer-front'],
    })
  } finally {
    data.isModalActionLoading = false
    closeModal()
  }
}

function scrollToTop() {
  if (scrollContainer.value?.scrollTo) {
    scrollContainer.value.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }
}

const openTimeoutToast = () => {
  openNeutralToast({
    title: i18n(translations.toastTimeoutTitle),
    content: i18n(translations.toastTimeoutContent),
    name: 'quotes-timeout-info-toast',
  })
}
</script>

<style module>
.opportunitiesCard {
  background-image:
    url('/img/buyback/opportunities.png'),
    linear-gradient(to right, #e2f77e, #e7dcf2);
  background-size: contain;
  background-repeat: no-repeat;
  background-position:
    center right 10%,
    left;
}
</style>
